import { Modal } from 'antd';
import { FC, useCallback, useState } from 'react';

import {
  ActualResidence,
  BirthPlace,
  GeneralData,
  IdentityCard,
  ProofOfActualResidenceData,
  ProofOfAddressData,
  RegisteredAddress,
} from '@/features/ui/ProfileChapters';

import { IntegrationSystem, SelectOption } from '@/shared/types';
import { Accordion, Typography } from '@/shared/ui';

import { StreetSelectOption, DocumentTypeEntity } from '@/api/types';

import { ProfileFormValues } from '../model';

import './ProfileStyles.scss';

interface ProfilePresentationalProps {
  reinitTriggerCount: number;
  onSubmitProfileData: (values: ProfileFormValues) => void;
  onResetForm: () => void;

  optionsCountry: SelectOption[];
  optionsRegion: SelectOption[];

  optionsDistrictReg: SelectOption[];
  optionsCityReg: SelectOption[];
  optionsStreetReg: StreetSelectOption[];

  optionsDistrictLiving: SelectOption[];
  optionsCityLiving: SelectOption[];
  optionsStreetLiving: StreetSelectOption[];

  passportDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  regAddressDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
  actualAddressDocumentTypeEntities: Array<DocumentTypeEntity<IntegrationSystem.ERZ>> | null;
}

export type FormHasDirtyFields =
  | 'general'
  | 'identity'
  | 'birth'
  | 'registered'
  | 'proofRegistered'
  | 'actual'
  | 'proofActual'
  | null;

const ProfilePresentational: FC<ProfilePresentationalProps> = ({
  reinitTriggerCount,
  onSubmitProfileData,
  onResetForm,

  optionsCountry,
  optionsRegion,

  optionsDistrictReg,
  optionsCityReg,
  optionsStreetReg,

  optionsDistrictLiving,
  optionsCityLiving,
  optionsStreetLiving,

  passportDocumentTypeEntities,
  actualAddressDocumentTypeEntities,
  regAddressDocumentTypeEntities,
}) => {
  const [activeKey, setActiveKey] = useState<Array<NonNullable<FormHasDirtyFields>>>(['general']);
  const [formHasDirtyFields, setFormHasDirtyFields] = useState<FormHasDirtyFields>(null);
  const [triggerResetCount, setTriggerResetCount] = useState<number>(0);

  const handleAccordionChange = useCallback(
    (newKey: NonNullable<FormHasDirtyFields>) => {
      if (activeKey.includes(newKey)) return;

      if (formHasDirtyFields !== null) {
        Modal.confirm({
          title: 'Ви не зберегли дані, які редагували',
          content:
            'У вас є незбережені зміни. При перемиканні змінені поля будуть відновлені до попереднього стану, а інші залишаться без змін. Продовжити?',
          onOk: () => {
            setActiveKey([newKey]);
            setFormHasDirtyFields(null);
            setTriggerResetCount((prev) => prev + 1);
          },
        });
      } else {
        setActiveKey([newKey]);
      }
    },
    [activeKey, formHasDirtyFields, triggerResetCount],
  );

  return (
    <div className="profile-presentational">
      <Accordion
        className="profile-presentational__accordion"
        // @ts-ignore
        onChange={(key): void => handleAccordionChange(key as string)}
        activeKey={activeKey}
        items={[
          {
            key: 'general',
            label: <Typography variant="H3">Загальні дані</Typography>,
            children: <GeneralData activeKey={activeKey} setFormHasDirtyFields={setFormHasDirtyFields} />,
          },
          {
            key: 'identity',
            label: <Typography variant="H3">Документ, що посвідчує особу</Typography>,
            children: (
              <IdentityCard
                setFormHasDirtyFields={setFormHasDirtyFields}
                activeKey={activeKey}
                passportDocumentTypeEntities={passportDocumentTypeEntities}
                reinitTriggerCount={reinitTriggerCount}
                optionsCountry={optionsCountry}
              />
            ),
          },
          {
            key: 'birth',
            label: <Typography variant="H3">Місце народження</Typography>,
            children: (
              <BirthPlace
                onSubmit={onSubmitProfileData}
                onReset={onResetForm}
                setFormHasDirtyFields={setFormHasDirtyFields}
                activeKey={activeKey}
              />
            ),
          },
          {
            key: 'registered',
            label: <Typography variant="H3">Зареєстроване місце проживання</Typography>,
            children: (
              <RegisteredAddress
                onSubmit={onSubmitProfileData}
                onReset={onResetForm}
                optionsCountry={optionsCountry}
                optionsRegion={optionsRegion}
                optionsDistrict={optionsDistrictReg}
                optionsCity={optionsCityReg}
                optionsStreet={optionsStreetReg}
                setFormHasDirtyFields={setFormHasDirtyFields}
                activeKey={activeKey}
              />
            ),
          },
          {
            key: 'proofRegistered',
            label: <Typography variant="H3">Дані документа, що підтверджує зареєстроване місце проживання</Typography>,
            children: (
              <ProofOfAddressData
                onSubmit={onSubmitProfileData}
                onReset={onResetForm}
                regAddressDocumentTypeEntities={regAddressDocumentTypeEntities}
                setFormHasDirtyFields={setFormHasDirtyFields}
                activeKey={activeKey}
              />
            ),
          },
          {
            key: 'actual',
            label: <Typography variant="H3">Фактичне місце проживання</Typography>,
            children: (
              <ActualResidence
                onSubmit={onSubmitProfileData}
                onReset={onResetForm}
                optionsCountry={optionsCountry}
                optionsRegion={optionsRegion}
                optionsDistrict={optionsDistrictLiving}
                optionsCity={optionsCityLiving}
                optionsStreet={optionsStreetLiving}
                setFormHasDirtyFields={setFormHasDirtyFields}
                activeKey={activeKey}
              />
            ),
          },
          {
            key: 'proofActual',
            label: <Typography variant="H3">Дані документа, що підтверджує фактичне місце проживання</Typography>,
            children: (
              <ProofOfActualResidenceData
                onSubmit={onSubmitProfileData}
                onReset={onResetForm}
                actualAddressDocumentTypeEntities={actualAddressDocumentTypeEntities}
                setFormHasDirtyFields={setFormHasDirtyFields}
                activeKey={activeKey}
              />
            ),
          },
        ]}
      />
    </div>
  );
};
export default ProfilePresentational;
